import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import CitySelect from '../components/CitySelect';
import { ArrowLeft, ArrowRight, CheckCircle, FileCheck, ShieldCheck, Send, Clock, Award } from 'lucide-react';
import { toast } from 'sonner';

const TaxServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useContext(AuthContext);

  const [service, setService] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', pan: '', city: '', annual_income: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v) sessionStorage.setItem(k, v);
    });
    axios.get(`${API}/tax-services/catalog`).then((r) => {
      const found = (r.data.services || []).find((s) => s.slug === slug);
      setService(found);
      if (found && found.plans && found.plans.length) setSelectedPlan(found.plans[0]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.full_name || '',
        email: f.email || user.email || '',
        phone: f.phone || user.phone || '',
      }));
    }
  }, [user]);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) { toast.error('Please pick a plan'); return; }
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill name, email & phone');
      return;
    }
    if (!/^\d{10}$/.test(form.phone)) {
      toast.error('Enter a valid 10-digit phone');
      return;
    }
    if (form.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.pan)) {
      toast.error('PAN must be in format ABCDE1234F');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        pan: form.pan || null,
        city: form.city || null,
        annual_income: form.annual_income ? Number(form.annual_income) : null,
        notes: form.notes || null,
        service_type: selectedPlan.service_type,
        utm_source: sessionStorage.getItem('utm_source') || null,
        utm_medium: sessionStorage.getItem('utm_medium') || null,
        utm_campaign: sessionStorage.getItem('utm_campaign') || null,
        lead_source: 'myarthik',
      };
      const token = localStorage.getItem('arthik_token');
      const res = await axios.post(`${API}/tax-cases`, payload, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      setDone(res.data);
      toast.success('Request received! Our tax expert will call you within 4 hours.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) {
    return (
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ padding: '5rem 1.5rem', textAlign: 'center', color: '#475569' }}>Loading...</div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
        <Navbar />
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '5rem 1.5rem' }}>
          <div className="calculator-section" style={{ textAlign: 'center', padding: '3rem 2rem' }} data-testid="tax-case-success">
            <CheckCircle size={72} style={{ color: '#10B981', margin: '0 auto 1.5rem' }} />
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', color: '#0F172A', marginBottom: '0.5rem' }}>Request Received</h2>
            <p style={{ color: '#475569', marginBottom: '1rem', fontSize: '1.05rem' }}>
              <strong>Reference:</strong> <span style={{ fontFamily: 'monospace', color: '#FF6B35', fontWeight: 700, fontSize: '1.15rem' }}>{done.id.slice(0, 8)}</span>
            </p>
            <p style={{ color: '#475569', marginBottom: '2rem', fontSize: '1rem', lineHeight: 1.6 }}>
              Our tax expert will call you on <strong>{done.phone}</strong> within <strong>4 working hours</strong>. We'll also send detailed instructions to <strong>{done.email}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {user && <button className="outline-button" onClick={() => navigate('/dashboard')} data-testid="tax-track-button">Track My Case</button>}
              <button className="primary-button" onClick={() => navigate('/tax-services')} data-testid="tax-back-button">Browse More Services</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }} data-testid={`tax-detail-${slug}`}>
      <Navbar />

      {/* Hero */}
      <section style={{ background: `linear-gradient(135deg, ${service.color}15 0%, #FFFFFF 100%)`, padding: '3.5rem 1.5rem 2rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <button onClick={() => navigate('/tax-services')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: service.color, cursor: 'pointer', marginBottom: '1.25rem', fontWeight: 600 }} data-testid="tax-back-link">
            <ArrowLeft size={18} /> All Tax Services
          </button>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#0F172A', margin: 0, fontWeight: 800, lineHeight: 1.15 }}>{service.title}</h1>
          <p style={{ fontSize: '1.1rem', color: '#475569', marginTop: '0.75rem' }}>{service.tagline}</p>
          <div style={{ display: 'flex', gap: '1.25rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0F172A', fontWeight: 600, fontSize: '0.875rem' }}><ShieldCheck size={16} style={{ color: '#10B981' }} /> Qualified CA</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0F172A', fontWeight: 600, fontSize: '0.875rem' }}><Clock size={16} style={{ color: '#FF6B35' }} /> 24-48 hrs</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0F172A', fontWeight: 600, fontSize: '0.875rem' }}><Award size={16} style={{ color: '#3B82F6' }} /> 100% accuracy</span>
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: '2rem' }} className="tax-detail-grid">
          {/* Plans */}
          <div>
            <h2 style={{ fontSize: '1.4rem', color: '#0F172A', marginBottom: '1rem' }}>Choose your plan</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {service.plans.map((p) => (
                <div
                  key={p.service_type}
                  onClick={() => setSelectedPlan(p)}
                  data-testid={`plan-${p.service_type}`}
                  style={{
                    padding: '1.5rem',
                    background: 'white',
                    border: selectedPlan?.service_type === p.service_type ? `2px solid ${service.color}` : '2px solid #E2E8F0',
                    borderRadius: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedPlan?.service_type === p.service_type ? `0 8px 24px ${service.color}25` : 'none',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                    <div>
                      <h3 style={{ fontSize: '1.15rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>{p.name}</h3>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.5rem', color: service.color, fontWeight: 800 }}>₹{p.price.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.4rem' }}>
                    {p.features.map((f, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#475569', fontSize: '0.9rem' }}>
                        <CheckCircle size={16} style={{ color: service.color, flexShrink: 0, marginTop: 2 }} /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '2rem', padding: '1.25rem', background: '#F8FAFC', borderRadius: '1rem', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem' }}>
                <FileCheck size={18} style={{ color: service.color }} />
                <h4 style={{ fontSize: '0.95rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>Documents you'll need</h4>
              </div>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#475569', fontSize: '0.875rem', lineHeight: 1.7 }}>
                {service.documents_required.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
              <p style={{ marginTop: '0.75rem', color: '#64748B', fontSize: '0.8rem', marginBottom: 0 }}>You'll receive a secure upload link by email after submitting this form.</p>
            </div>
          </div>

          {/* Lead form */}
          <div>
            <form className="calculator-section" onSubmit={submit} data-testid="tax-lead-form" style={{ padding: '2rem', position: 'sticky', top: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', color: '#0F172A', margin: 0, marginBottom: '0.25rem', fontWeight: 700 }}>Get started</h3>
              <p style={{ color: '#64748B', fontSize: '0.875rem', marginBottom: '1.25rem' }}>Our tax expert will call you within 4 hours.</p>

              {selectedPlan && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: `${service.color}10`, borderLeft: `4px solid ${service.color}`, borderRadius: 8 }}>
                  <div style={{ fontSize: '0.75rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>Selected plan</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ fontWeight: 700, color: '#0F172A' }}>{selectedPlan.name}</div>
                    <div style={{ color: service.color, fontWeight: 700 }}>₹{selectedPlan.price.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div>
                  <label className="label-text">Full Name *</label>
                  <input className="input-field" value={form.name} onChange={handleChange('name')} placeholder="Your name" data-testid="tax-name" />
                </div>
                <div>
                  <label className="label-text">Email *</label>
                  <input className="input-field" type="email" value={form.email} onChange={handleChange('email')} placeholder="you@example.com" data-testid="tax-email" />
                </div>
                <div>
                  <label className="label-text">Phone (10 digits) *</label>
                  <input className="input-field" value={form.phone} onChange={handleChange('phone')} placeholder="9876543210" maxLength={10} data-testid="tax-phone" />
                </div>
                <div>
                  <label className="label-text">PAN (optional)</label>
                  <input className="input-field" value={form.pan} onChange={handleChange('pan')} placeholder="ABCDE1234F" maxLength={10} style={{ textTransform: 'uppercase' }} data-testid="tax-pan" />
                </div>
                <div>
                  <label className="label-text">City (optional)</label>
                  <CitySelect value={form.city} onChange={(v) => setForm({ ...form, city: v })} testId="tax-city" />
                </div>
                {slug === 'itr-filing' && (
                  <div>
                    <label className="label-text">Annual Income ₹ (optional)</label>
                    <input className="input-field" type="number" value={form.annual_income} onChange={handleChange('annual_income')} placeholder="800000" data-testid="tax-income" />
                  </div>
                )}
                <div>
                  <label className="label-text">Notes (optional)</label>
                  <textarea className="input-field" rows={2} value={form.notes} onChange={handleChange('notes')} placeholder="Anything we should know?" data-testid="tax-notes" />
                </div>
              </div>

              <button type="submit" disabled={submitting} className="primary-button" style={{ marginTop: '1.25rem', width: '100%', justifyContent: 'center', padding: '0.9rem 1rem', fontSize: '1rem' }} data-testid="tax-submit">
                <Send size={18} /> {submitting ? 'Submitting...' : 'Get Started'} <ArrowRight size={16} />
              </button>
              <p style={{ marginTop: '0.75rem', color: '#94A3B8', fontSize: '0.75rem', textAlign: 'center' }}>
                100% secure. No spam. Pay only after CA review.
              </p>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .tax-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default TaxServiceDetailPage;
